import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Eye, CurrencyInr, Check, DownloadSimple, Printer, Receipt, PencilSimple, Trash, MagnifyingGlass, Ticket, DotsThreeVertical, BookOpen, CaretRight, CaretDown, LinkSimple, Copy, WhatsappLogo, CreditCard } from '@phosphor-icons/react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import InvoiceLedgerDialog from '@/components/InvoiceLedgerDialog';
import CustomerLedgerDialog from '@/components/CustomerLedgerDialog';
import PaymentReceiptDialog from '@/components/PaymentReceiptDialog';
import { toast } from 'sonner';
import { printInvoice as sharedPrintInvoice } from '@/lib/printInvoice';
import { useCan } from '@/lib/permissions';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

const statusColors = {
  paid: 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20',
  partial: 'bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/20',
  pending: 'bg-[#EF4444]/10 text-[#EF4444] border-[#EF4444]/20',
  unpaid: 'bg-[#EF4444]/10 text-[#EF4444] border-[#EF4444]/20',
  overpaid: 'bg-[#8B5CF6]/10 text-[#8B5CF6] border-[#8B5CF6]/20',
};
const workColors = {
  pending: 'bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/20',
  in_process: 'bg-[#3B82F6]/10 text-[#3B82F6] border-[#3B82F6]/20',
  completed: 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20',
};
const statusLabel = { paid: 'Paid', partial: 'Partial', pending: 'Credit', unpaid: 'Credit', overpaid: 'Overpaid' };
const workLabel = { pending: 'Pending', in_process: 'In Process', completed: 'Submitted' };

export default function InvoicesPage() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [filter, setFilter] = useState('all');
  const [workFilter, setWorkFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [showClearTokens, setShowClearTokens] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  // Bulk-delete: tracks which invoice IDs are ticked. Only rows that pass
  // `canDeleteRow` (no payments recorded) are selectable.
  const [bulkSelected, setBulkSelected] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  // {customer_id: phone} lookup so each invoice / draft row can show the
  // customer's mobile number underneath their name without N+1 fetches.
  const [phoneByCustomer, setPhoneByCustomer] = useState({});
  // Company display name — used in the pre-filled WhatsApp message so the
  // recipient instantly recognises the sender. Falls back to a neutral
  // string when /settings/company isn't reachable.
  const [companyName, setCompanyName] = useState('');
  const [razorpayReady, setRazorpayReady] = useState(false);
  // Admin-editable WhatsApp message template — populated from
  // /settings/company on mount. Placeholders are substituted at click time.
  const [waTemplate, setWaTemplate] = useState('');
  const [payDialog, setPayDialog] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [payMode, setPayMode] = useState('');
  const [payDate, setPayDate] = useState(new Date().toISOString().split('T')[0]);
  const [detailDialog, setDetailDialog] = useState(null);
  const [ledgerInvoice, setLedgerInvoice] = useState(null);
  // Customer-ledger popup, opened by clicking any customer name in this page.
  // Stays on /invoices on close — no navigation away.
  const [ledgerCustomer, setLedgerCustomer] = useState(null);
  const [finAccounts, setFinAccounts] = useState([]);
  // Staff directory — { id: name } map used to render the "Assigned To"
  // column on each invoice row.
  const [staffById, setStaffById] = useState({});
  // Service directory — { id: { link1 } } map used to render the per-service
  // quick-link icon next to the service name in the invoice list rows.
  const [serviceLinkById, setServiceLinkById] = useState({});
  // Which day / month groups are currently expanded in the history view.
  // We default to expanding today's day so the most relevant slice is visible
  // on every load — everything else collapses behind a +/− chevron.
  const _todayKey = new Date().toISOString().split('T')[0];
  const [expandedDays, setExpandedDays] = useState(() => new Set([_todayKey]));
  // Same idea for the Draft Tasks panel — drafts are grouped by their save
  // date and only today's bucket is open by default.
  const [expandedDraftDays, setExpandedDraftDays] = useState(() => new Set([_todayKey]));
  const [expandedMonths, setExpandedMonths] = useState(() => new Set()); // 'YYYY-MM'

  const toggleDay = (key) => setExpandedDays(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const toggleMonth = (key) => setExpandedMonths(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const canEdit = useCan('sales', 'edit');
  const canDelete = useCan('sales', 'delete');
  const canSeeSummary = useCan('sales_summary', 'view');
  const [showSummary, setShowSummary] = useState(() => localStorage.getItem('invoices_show_summary') !== '0');
  const toggleShowSummary = (v) => { setShowSummary(v); localStorage.setItem('invoices_show_summary', v ? '1' : '0'); };
  const summaryVisible = canSeeSummary && showSummary;

  const sharePayLink = async (inv, waPhone) => {
    try {
      const { data } = await api.post(`/invoices/${inv.id}/pay-link`);
      const url = `${window.location.origin}/pay/${data.token}`;
      const msg = `Hello ${inv.customer_name || ''},\n\nPlease pay ₹${Number(inv.balance_due || 0)} for bill ${inv.invoice_number} from ${companyName || 'our centre'} securely online:\n${url}\n\nThank you!`;
      try { await navigator.clipboard.writeText(url); } catch { /* clipboard may be blocked */ }
      if (waPhone) {
        window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(msg)}`, '_blank', 'noopener');
        toast.success('Payment link copied & WhatsApp opened');
      } else {
        toast.success('Payment link copied to clipboard');
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Could not create payment link');
    }
  };

  const fetchInvoices = useCallback(() => {
    const params = {};
    if (filter !== 'all') params.status = filter;
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    api.get('/invoices', { params }).then(r => setInvoices(r.data)).catch(console.error).finally(() => setLoading(false));
  }, [filter, fromDate, toDate]);

  const [counts, setCounts] = useState({ invoices: 0, drafts: 0 });
  const fetchDrafts = useCallback(() => {
    api.get('/drafts').then(r => setDrafts(r.data)).catch(console.error);
    api.get('/invoices/count').then(r => setCounts(r.data)).catch(() => {});
  }, []);

  const fetchTokens = useCallback(() => {
    api.get('/tokens').then(r => setTokens(r.data)).catch(() => setTokens([]));
  }, []);

  useEffect(() => { fetchInvoices(); fetchDrafts(); fetchTokens(); }, [fetchInvoices, fetchDrafts, fetchTokens]);
  // Lightweight polling so cross-page mutations (token deletion restoring a
  // draft, another operator creating an invoice, etc.) show up automatically
  // within seconds — no manual reload needed. Only fires when the tab is
  // visible so we don't waste bandwidth on backgrounded pages.
  useEffect(() => {
    const POLL_MS = 8000;
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      fetchInvoices(); fetchDrafts(); fetchTokens();
    };
    const timer = setInterval(refresh, POLL_MS);
    const onVis = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onVis); };
  }, [fetchInvoices, fetchDrafts, fetchTokens]);
  useEffect(() => {
    // One-shot customer phone map for the invoice / draft rows. Cached for
    // the lifetime of the page mount — the customer list is small enough
    // that refetching on every navigation isn't worth the round-trip.
    api.get('/customers').then(r => {
      const map = {};
      (r.data || []).forEach(c => { if (c.id) map[c.id] = c.phone || ''; });
      setPhoneByCustomer(map);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    // Cache the company name once so the WhatsApp prefilled message can
    // identify the sender. Quietly no-ops if the endpoint is unreachable.
    api.get('/settings/company').then(r => {
      setCompanyName(r.data?.name || '');
      setWaTemplate(r.data?.whatsapp_template || '');
      setRazorpayReady(!!r.data?.razorpay_configured);
    }).catch(() => {});
  }, []);
  useEffect(() => { api.get('/finance/accounts').then(r => { setFinAccounts(r.data); if (!payMode && r.data.length) { const first = r.data.find(a => a.type === 'cash' || a.type === 'bank'); if (first) setPayMode(first.id); } }).catch(console.error); }, []);
  useEffect(() => {
    // Build {id: name} map so each invoice row can show its first assigned-to staff member.
    api.get('/work/staff').then(r => {
      const map = {};
      (r.data || []).forEach(s => { map[s.id] = s.name; });      setStaffById(map);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    // Pull service catalog so each row can surface the per-service `link1`
    // quick-shortcut icon next to the service name (opens in a new tab).
    api.get('/services').then(r => {
      const map = {};
      (r.data || []).forEach(s => { map[s.id] = s.link1 || ''; });
      setServiceLinkById(map);
    }).catch(() => {});
  }, []);

  const handlePay = async () => {
    if (!payAmount || parseFloat(payAmount) <= 0) return;
    try {
      await api.put(`/invoices/${payDialog.id}/payment`, { amount: parseFloat(payAmount), payment_mode: payMode, payment_date: payDate });
      toast.success('Payment recorded');
      setPayDialog(null);
      setPayAmount('');
      setPayDate(new Date().toISOString().split('T')[0]);
      fetchInvoices();
    } catch { toast.error('Failed to record payment'); }
  };

  const toggleWork = async (inv) => {
    const order = ['pending', 'in_process', 'completed'];
    const curr = order.indexOf(inv.work_status || 'pending');
    const next = order[(curr + 1) % order.length];
    await api.put(`/invoices/${inv.id}/work-status`, { work_status: next });
    fetchInvoices();
  };

  const handleDeleteDraft = async (d) => {
    setConfirmDelete({ type: 'draft', item: d, label: `Delete draft for ${d.customer_name}?` });
  };

  const handleDelete = async (inv) => {
    setConfirmDelete({ type: 'invoice', item: inv, label: `Delete invoice ${inv.invoice_number}? This will reverse balances and remove transactions.` });
  };

  const handleMakeDraft = async (inv) => {
    if (!window.confirm(`Convert ${inv.invoice_number} back to a draft? Any payments recorded against it will be reversed.`)) return;
    try {
      await api.post(`/invoices/${inv.id}/make-draft`);
      toast.success(`${inv.invoice_number} converted to draft`);
      fetchInvoices(); fetchDrafts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to convert to draft');
    }
  };

  const confirmDeleteAction = async () => {
    if (!confirmDelete) return;
    try {
      if (confirmDelete.type === 'draft') {
        await api.delete(`/drafts/${confirmDelete.item.id}`);
        toast.success('Draft deleted');
        fetchDrafts();
      } else {
        await api.delete(`/invoices/${confirmDelete.item.id}`);
        toast.success(`Invoice ${confirmDelete.item.invoice_number} deleted`);
        fetchInvoices();
      }
    } catch { toast.error('Failed to delete'); }
    setConfirmDelete(null);
  };

  // Same business rule as the row-level Delete in the actions menu — only
  // invoices with no payments at all are eligible for deletion. Surfaced
  // here so the bulk-select checkbox can be disabled on guarded rows.
  const canDeleteRow = (inv) =>
    canDelete && !((inv.amount_paid || 0) > 0 || (inv.payment_history && inv.payment_history.length > 0));

  const performBulkDelete = async () => {
    if (bulkSelected.size === 0) return;
    setBulkDeleting(true);
    let ok = 0, failed = 0;
    for (const id of bulkSelected) {
      try {
        await api.delete(`/invoices/${id}`);
        ok++;
      } catch {
        failed++;
      }
    }
    setBulkDeleting(false);
    setConfirmBulkDelete(false);
    setBulkSelected(new Set());
    if (ok > 0) toast.success(`Deleted ${ok} invoice${ok === 1 ? '' : 's'}${failed ? ` (${failed} failed)` : ''}`);
    if (ok === 0 && failed > 0) toast.error(`Failed to delete ${failed} invoice${failed === 1 ? '' : 's'}`);
    fetchInvoices();
  };

  const exportExcel = async () => {
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const response = await api.get('/invoices/export-excel', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'invoices.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to export Excel'); }
  };

  const exportCSV = () => {
    const headers = ['Invoice', 'Customer', 'Services', 'Svc Charge', 'Govt Fee', 'Total', 'Outstanding', 'Payment', 'Work Status', 'Mode', 'Date'];
    const rows = filteredInvoices.map(inv => [
      inv.invoice_number, inv.customer_name,
      inv.items?.map(i => i.service_name).join('; '),
      inv.items?.reduce((s, i) => s + i.service_charge * i.quantity, 0),
      inv.items?.reduce((s, i) => s + i.govt_fee * i.quantity, 0),
      inv.total, inv.balance_due, inv.status, inv.work_status || 'pending', inv.payment_mode,
      new Date(inv.created_at).toLocaleDateString('en-IN'),
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'invoices.csv';
    a.click();
  };

  // Delegates to the shared `printInvoice` helper (`lib/printInvoice.js`)
  // so the Actions → Print preview matches the Edit + Create/Post-save
  // previews exactly. Passing the raw invoice row is enough — the helper
  // fetches the customer + company on its own.
  const printInvoice = (inv) => sharedPrintInvoice({ invoice: inv, finAccounts });

  const filteredInvoices = (() => {
    let list = workFilter === 'all' ? invoices : invoices.filter(i => (i.work_status || 'pending') === workFilter);
    const q = search.trim().toLowerCase();
    const qDigits = q.replace(/\D/g, '');
    if (q) {
      list = list.filter(i =>
        (i.customer_name || '').toLowerCase().includes(q) ||
        (i.items || []).some(it => (it.service_name || '').toLowerCase().includes(q)) ||
        (i.invoice_number || '').toLowerCase().includes(q) ||
        (qDigits.length >= 3 && String(phoneByCustomer[i.customer_id] || '').replace(/\D/g, '').includes(qDigits))
      );
    }
    return list;
  })();

  // Apply the same search term to the Draft Tasks panel so the user can
  // narrow drafts by customer / service / reason / preserved invoice number
  // from the single search box at the top of the page.
  const filteredDrafts = (() => {
    const q = search.trim().toLowerCase();
    if (!q) return drafts;
    return drafts.filter(d =>
      (d.customer_name || '').toLowerCase().includes(q) ||
      (d.items || []).some(it => (it.service_name || '').toLowerCase().includes(q)) ||
      (d.reason || '').toLowerCase().includes(q) ||
      (d.invoice_number || '').toLowerCase().includes(q) ||
      (d.from_invoice_number || '').toLowerCase().includes(q)
    );
  })();

  // Summary cards mirror the CURRENTLY VISIBLE list so the numbers always
  // move in sync with the filters the operator has applied (payment status
  // buttons, work status buttons, search box). Previously the cards used
  // the raw `invoices` array and only reflected the backend-side date/
  // status filters, giving the illusion that filters "didn't work".
  const totalCollected = filteredInvoices.reduce((s, i) => s + i.amount_paid, 0);
  const totalOutstanding = filteredInvoices.reduce((s, i) => s + i.balance_due, 0);
  const pendingWork = filteredInvoices.filter(i => (i.work_status || 'pending') !== 'completed').length;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="invoices-page">
      <div className="flex items-center justify-end">
        {canEdit && (
          <Button onClick={() => navigate('/invoices/create')} className="bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-xl" data-testid="create-invoice-btn">
            <Plus size={16} className="mr-2" /> Create Invoice
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="rounded-xl border-[#E4E4E7] shadow-sm"><CardContent className="p-4"><p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#52525B]">Total Invoices</p><p className="text-xl font-bold mt-1" style={{ fontFamily: 'var(--font-heading)' }} data-testid="total-invoices-count">{counts.invoices}<span className="text-xs font-normal text-[#A1A1AA] ml-1.5" data-testid="total-drafts-count">· {counts.drafts} draft{counts.drafts === 1 ? '' : 's'}</span></p></CardContent></Card>
        <Card className="rounded-xl border-[#E4E4E7] shadow-sm"><CardContent className="p-4"><p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#52525B]">Collected</p><p className="text-xl font-bold mt-1 text-[#10B981]" style={{ fontFamily: 'var(--font-heading)' }}>{fmt(totalCollected)}</p></CardContent></Card>
        <Card className="rounded-xl border-[#E4E4E7] shadow-sm"><CardContent className="p-4"><p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#52525B]">{totalOutstanding < 0 ? 'Advance Held' : 'Outstanding'}</p><p className="text-xl font-bold mt-1" style={{ fontFamily: 'var(--font-heading)', color: totalOutstanding < 0 ? '#8B5CF6' : '#EA580C' }}>{fmt(Math.abs(totalOutstanding))}</p></CardContent></Card>
        <Card className="rounded-xl border-[#E4E4E7] shadow-sm"><CardContent className="p-4"><p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#52525B]">Pending Work</p><p className="text-xl font-bold mt-1 text-[#F59E0B]" style={{ fontFamily: 'var(--font-heading)' }}>{pendingWork}</p></CardContent></Card>
      </div>

      {/* Pending Tokens */}
      {tokens.length > 0 && (
        <Card className="rounded-xl border-[#10B981]/40 bg-gradient-to-r from-[#ECFDF5] to-white shadow-sm" data-testid="tokens-panel">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Ticket size={16} weight="fill" className="text-[#10B981]" />
                <Badge className="rounded-md text-[10px] bg-[#10B981]/20 text-[#047857] border-0" variant="outline">Token</Badge>
                <span className="text-xs text-[#52525B]">{tokens.length} pending token{tokens.length > 1 ? 's' : ''}</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowClearTokens(true)}
                className="rounded-lg h-7 text-[11px] border-[#EF4444] text-[#EF4444] hover:bg-red-50 hover:text-[#EF4444]"
                data-testid="clear-tokens-btn"
              >
                <Trash size={12} className="mr-1" /> Clear all (Day End)
              </Button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {tokens.map((t) => {
                // Completed tokens — staff has already routed it to either a
                // Draft or a new Invoice. The chip stays visible for the
                // admin's day-end view but actions are locked (delete only).
                const isCompleted = t.status === 'completed';
                return (
                <div
                  key={t.id}
                  className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs transition-colors ${
                    isCompleted
                      ? 'border-[#A1A1AA]/30 bg-[#F4F4F5] opacity-70'
                      : 'border-[#10B981]/40 bg-white hover:border-[#10B981]'
                  }`}
                  data-testid={`token-chip-${t.token_number}`}
                >
                  <span className={`font-mono font-bold ${isCompleted ? 'text-[#71717A] line-through' : 'text-[#10B981]'}`}>{t.token_number}</span>
                  {isCompleted && (
                    <span className="px-1 py-0.5 rounded bg-[#71717A]/15 text-[#52525B] text-[9px] font-semibold uppercase tracking-wider" data-testid={`token-completed-${t.token_number}`}>
                      Done
                    </span>
                  )}
                  {/* Customer name opens the ledger popup inline — no
                      navigation away from the Invoices page. */}
                  {t.customer_id ? (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setLedgerCustomer({ id: t.customer_id, name: t.customer_name }); }}
                      className="truncate max-w-[140px] text-[#52525B] hover:text-[#3B82F6] hover:underline cursor-pointer"
                      title={`Open ledger for ${t.customer_name}`}
                      data-testid={`token-customer-link-${t.id}`}
                    >
                      {t.customer_name}
                    </button>
                  ) : (
                    <span className="truncate max-w-[140px] text-[#52525B]">{t.customer_name}</span>
                  )}
                  <span className="ml-1 font-semibold text-[#0A0A0A]">{fmt(t.total || 0)}</span>
                  {/* Edit is hidden once a token is completed — admin can
                      no longer convert it via this chip (the conversion
                      already happened on My Work). */}
                  {canEdit && !isCompleted && (
                    <button
                      type="button"
                      onClick={() => navigate(`/invoices/create?token=${t.id}`)}
                      className="ml-1 p-0.5 rounded hover:bg-[#ECFDF5] text-[#10B981]"
                      title="Edit / convert to invoice"
                      data-testid={`token-edit-${t.token_number}`}
                    >
                      <PencilSimple size={12} />
                    </button>
                  )}
                  {canDelete && (
                    <button
                      type="button"
                      onClick={async () => {
                        try { await api.delete(`/tokens/${t.id}`); toast.success(`${t.token_number} deleted`); fetchTokens(); }
                        catch { toast.error('Failed to delete token'); }
                      }}
                      className="p-0.5 rounded hover:bg-red-50 text-[#EF4444]"
                      title="Delete token"
                      data-testid={`token-delete-${t.token_number}`}
                    >
                      <Trash size={12} />
                    </button>
                  )}
                </div>
              );})}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bulk Delete confirmation. */}
      <AlertDialog open={confirmBulkDelete} onOpenChange={(v) => { if (!v && !bulkDeleting) setConfirmBulkDelete(false); }}>
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {bulkSelected.size} invoice{bulkSelected.size === 1 ? '' : 's'}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the selected invoices and reverses their finance ledger entries. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting} className="rounded-lg" data-testid="bulk-delete-invoices-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={bulkDeleting}
              onClick={performBulkDelete}
              className="bg-[#EF4444] text-white hover:bg-[#DC2626] rounded-lg disabled:opacity-50"
              data-testid="bulk-delete-invoices-confirm"
            >
              {bulkDeleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showClearTokens} onOpenChange={setShowClearTokens}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear all pending tokens?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <b>{tokens.length}</b> pending token{tokens.length === 1 ? '' : 's'}. Use this at day-end to reset the serial back to T001.
              Already-converted invoices are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={async () => {
                try { const r = await api.post('/tokens/clear-all'); toast.success(`Cleared ${r.data.deleted} tokens`); fetchTokens(); }
                catch { toast.error('Failed to clear tokens'); }
                setShowClearTokens(false);
              }}
              data-testid="clear-tokens-confirm"
            >
              Clear all tokens
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Filters — placed above Draft Tasks so the user can search /
          filter / export from a single sticky-feeling toolbar before
          diving into pending drafts. All controls share one wrapping
          flex row; min-w-0 lets the search shrink gracefully on narrow
          viewports while the export buttons hug the right edge. */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search customer, mobile, service or invoice…"
            onKeyDown={e => { if (e.key === 'Escape') { setSearch(''); e.currentTarget.blur(); } }}
            className="h-7 pl-7 rounded-lg text-xs w-64" data-testid="invoice-search-input" />
        </div>
        {canSeeSummary && (
          <label className="flex items-center gap-1.5 text-[11px] text-[#52525B] cursor-pointer select-none whitespace-nowrap" data-testid="toggle-summary-label">
            <input type="checkbox" checked={showSummary} onChange={e => toggleShowSummary(e.target.checked)} className="h-3.5 w-3.5 accent-[#3B82F6]" data-testid="toggle-summary-checkbox" />
            Show Billed / Due totals
          </label>
        )}
        <div className="flex gap-1">
          {['all', 'paid', 'partial', 'unpaid', 'overpaid'].map(s => (
            <Button key={s} variant={filter === s ? 'default' : 'outline'} size="sm"
              className={`rounded-lg text-[10px] h-7 px-2 capitalize ${filter === s ? 'bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white' : 'border-[#E4E4E7] hover:bg-[#F4F4F5]'}`}
              onClick={() => setFilter(s)} data-testid={`filter-${s}`}>{s === 'all' ? 'All' : statusLabel[s] || s}</Button>
          ))}
        </div>
        <div className="h-5 w-px bg-[#E4E4E7]" />
        <div className="flex gap-1">
          {['all', 'pending', 'in_process', 'completed'].map(s => (
            <Button key={s} variant={workFilter === s ? 'default' : 'outline'} size="sm"
              className={`rounded-lg text-[10px] h-7 px-2 capitalize ${workFilter === s ? 'bg-[#0A0A0A] text-white hover:bg-black/80 hover:text-white' : 'border-[#E4E4E7] hover:bg-[#F4F4F5]'}`}
              onClick={() => setWorkFilter(s)} data-testid={`work-filter-${s}`}>{s === 'all' ? 'All' : workLabel[s] || s}</Button>
          ))}
        </div>
        <div className="h-5 w-px bg-[#E4E4E7]" />
        <div className="flex items-center gap-1.5">
          <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="h-7 rounded-lg text-xs w-32" data-testid="from-date-input" />
          <span className="text-xs text-[#52525B]">to</span>
          <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="h-7 rounded-lg text-xs w-32" data-testid="to-date-input" />
        </div>
        <div className="flex items-center gap-1.5 ml-auto">
          <Button variant="outline" size="sm" onClick={exportCSV} className="rounded-lg text-[10px] h-7 px-2 border-[#E4E4E7]" data-testid="export-csv-btn">
            <DownloadSimple size={12} className="mr-1" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={exportExcel} className="rounded-lg text-[10px] h-7 px-2 border-[#E4E4E7]" data-testid="export-excel-btn">
            <DownloadSimple size={12} className="mr-1" /> Excel
          </Button>
        </div>
      </div>

      {/* Draft Tasks */}
      {filteredDrafts.length > 0 && (
        <Card className="rounded-xl border-[#F59E0B]/40 bg-[#FFFBEB] shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Badge className="rounded-md text-[10px] bg-[#F59E0B]/20 text-[#D97706] border-0" variant="outline">Draft Task</Badge>
                <span className="text-xs text-[#52525B]">
                  {/* Show "X of Y" when the search has narrowed the list so the
                      user knows there are more drafts hidden by the filter. */}
                  {search.trim() && filteredDrafts.length !== drafts.length
                    ? `${filteredDrafts.length} of ${drafts.length} draft${drafts.length > 1 ? 's' : ''}`
                    : `${drafts.length} draft${drafts.length > 1 ? 's' : ''}`}
                </span>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="border-[#F59E0B]/20">
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Customer</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Invoice #</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Services</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Reason</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Svc Charge</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Govt Fee</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Total</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">By</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Date</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold w-20">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  // Group drafts by their save-date. Drafts from the last
                  // SEVEN days keep their own per-day bucket so the operator
                  // can see the natural day-by-day queue; anything older is
                  // clubbed into a single "Older" bucket at the bottom to
                  // keep the panel compact once history grows. Threshold is
                  // rolling — computed against local midnight so a day flip
                  // doesn't require a page reload.
                  const OLDER_KEY = '__older__';
                  const now = new Date();
                  const cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                  cutoff.setDate(cutoff.getDate() - 6);      // include today + previous 6 days = 7-day window
                  const cutoffKey = cutoff.toISOString().split('T')[0];
                  const byDay = new Map(); // dayKey → array of drafts
                  for (const d of filteredDrafts) {
                    const raw = (d.created_at || '').slice(0, 10) || 'unknown';
                    // Anything before the 7-day cutoff (or with an unknown
                    // date) collapses into the "Older" bucket.
                    const k = (raw === 'unknown' || raw < cutoffKey) ? OLDER_KEY : raw;
                    if (!byDay.has(k)) byDay.set(k, []);
                    byDay.get(k).push(d);
                  }
                  // Recent buckets sort newest first; "Older" always pins to
                  // the bottom regardless of natural sort order.
                  const dayKeys = Array.from(byDay.keys()).sort((a, b) => {
                    if (a === OLDER_KEY) return 1;
                    if (b === OLDER_KEY) return -1;
                    return b.localeCompare(a);
                  });
                  const todayKey = new Date().toISOString().split('T')[0];
                  const colSpan = 10;
                  const rows = [];
                  for (const dayKey of dayKeys) {
                    const list = byDay.get(dayKey);
                    const isOpen = expandedDraftDays.has(dayKey);
                    const label = (() => {
                      if (dayKey === OLDER_KEY) return 'Older (7+ days)';
                      if (!dayKey || dayKey === 'unknown') return '— Unknown date';
                      const dt = new Date(dayKey);
                      if (dayKey === todayKey) return 'Today';
                      return dt.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
                    })();
                    rows.push(
                      <TableRow key={`draft-day-${dayKey}`} className="bg-[#FEF3C7] hover:bg-[#FDE68A] cursor-pointer border-[#F59E0B]/20"
                        onClick={() => {
                          setExpandedDraftDays(prev => {
                            const n = new Set(prev);
                            if (n.has(dayKey)) n.delete(dayKey); else n.add(dayKey);
                            return n;
                          });
                        }}
                        data-testid={`draft-day-header-${dayKey}`}
                      >
                        <TableCell colSpan={colSpan} className="text-xs font-semibold text-[#92400E] py-2">
                          <div className="flex items-center gap-2">
                            {isOpen ? <CaretDown size={12} /> : <CaretRight size={12} />}
                            <span>{label}</span>
                            <Badge className="rounded-md text-[10px] bg-[#F59E0B]/20 text-[#D97706] border-0" variant="outline">
                              {list.length} draft{list.length === 1 ? '' : 's'}
                            </Badge>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                    if (isOpen) {
                      for (const d of list) {
                        rows.push(
                          <TableRow key={d.id} className="border-[#F59E0B]/10">
                            <TableCell className="text-sm font-medium">
                              {d.customer_id && d.customer_name ? (
                                <button
                                  type="button"
                                  onClick={() => setLedgerCustomer({ id: d.customer_id, name: d.customer_name })}
                                  className="text-[#0A0A0A] hover:text-[#3B82F6] hover:underline cursor-pointer text-left"
                                  title={`Open ledger for ${d.customer_name}`}
                                  data-testid={`draft-customer-link-${d.id}`}
                                >
                                  {d.customer_name}
                                </button>
                              ) : (d.customer_name || '-')}
                              {/* Applicant chip — only when it differs from
                                  the billed customer. Same rule as the
                                  Invoices table above. */}
                              {(() => {
                                const appl = (d.applicant_name || '').trim();
                                const cust = (d.customer_name || '').trim();
                                if (!appl || appl.toLowerCase() === cust.toLowerCase()) return null;
                                return (
                                  <div
                                    className="text-[10px] text-[#7C3AED] truncate mt-0.5"
                                    title={`Applicant: ${appl}`}
                                    data-testid={`draft-applicant-${d.id}`}
                                  >
                                    ({appl})
                                  </div>
                                );
                              })()}
                            </TableCell>
                            <TableCell className="text-xs font-mono font-semibold text-[#1D4ED8] whitespace-nowrap" data-testid={`draft-number-${d.id}`}>
                              {d.from_invoice_number ? (d.invoice_number || '').trim() : ''}
                            </TableCell>
                            <TableCell className="text-xs text-[#52525B]">
                              <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                                {(d.items || []).map((it, i) => {
                                  const link = serviceLinkById[it.service_id];
                                  return (
                                    <span key={i} className="inline-flex items-center gap-1">
                                      {link ? (
                                        <a href={link} target="_blank" rel="noopener noreferrer"
                                          title={`Open ${it.service_name} portal`}
                                          data-testid={`draft-link1-${d.id}-${i}`}
                                          className="inline-flex items-center justify-center w-5 h-5 rounded bg-[#E0F2FE] text-[#0369A1] hover:bg-[#BAE6FD]">
                                          <LinkSimple size={11} weight="bold" />
                                        </a>
                                      ) : null}
                                      <span>{it.service_name}{i < (d.items.length - 1) ? ',' : ''}</span>
                                    </span>
                                  );
                                })}
                              </div>
                            </TableCell>
                            <TableCell className="text-xs text-[#52525B] max-w-[280px] truncate" title={d.reason || ''} data-testid={`draft-reason-${d.id}`}>
                              {d.reason ? <span className="text-[#0A0A0A]">{d.reason}</span> : <span className="italic text-[#A1A1AA]">—</span>}
                            </TableCell>
                            <TableCell className="text-sm text-right font-mono text-[#10B981]">{fmt(d.total_svc_charge || 0)}</TableCell>
                            <TableCell className="text-sm text-right font-mono text-[#52525B]">{fmt(d.total_govt_fee || 0)}</TableCell>
                            <TableCell className="text-sm text-right font-mono font-semibold">{fmt(d.total || 0)}</TableCell>
                            <TableCell className="text-xs text-[#52525B]">{d.created_by_name || '-'}</TableCell>
                            <TableCell className="text-xs text-[#52525B]">{new Date(d.created_at).toLocaleDateString('en-IN')}</TableCell>
                            <TableCell>
                              <div className="flex gap-0.5">
                                {canEdit && (<Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-[#3B82F6] hover:text-[#2563EB]" onClick={() => navigate(`/invoices/create?draft=${d.id}`)} data-testid={`edit-draft-${d.id}`}><PencilSimple size={12} /></Button>)}
                                {canEdit && (
                                  <Button
                                    variant="ghost" size="sm"
                                    className="h-6 w-6 p-0 text-[#52525B] hover:text-[#7C3AED]"
                                    onClick={() => navigate(`/invoices/create?duplicate_from=draft:${d.id}`)}
                                    title="Duplicate as new invoice"
                                    data-testid={`duplicate-draft-${d.id}`}
                                  >
                                    <Copy size={12} />
                                  </Button>
                                )}
                                {canDelete && (<Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-[#52525B] hover:text-[#EF4444]" onClick={() => handleDeleteDraft(d)} data-testid={`delete-draft-${d.id}`}><Trash size={12} /></Button>)}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      }
                    }
                  }
                  return rows;
                })()}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}


      {/* Bulk action bar — only shows when at least one invoice is ticked. */}
      {bulkSelected.size > 0 && (
        <div className="flex items-center justify-between gap-3 px-4 py-2 rounded-lg bg-[#FEF2F2] border border-[#FCA5A5]" data-testid="invoice-bulk-bar">
          <p className="text-xs text-[#991B1B]">
            <span className="font-semibold">{bulkSelected.size}</span> invoice{bulkSelected.size === 1 ? '' : 's'} selected for bulk delete
          </p>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setBulkSelected(new Set())} className="text-xs h-7" data-testid="bulk-clear-invoices">
              Clear
            </Button>
            <Button size="sm" onClick={() => setConfirmBulkDelete(true)} className="bg-[#EF4444] text-white hover:bg-[#DC2626] hover:text-white rounded-lg text-xs h-7" data-testid="bulk-delete-invoices-btn">
              <Trash size={12} className="mr-1" /> Delete Selected
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <Card className="rounded-xl border-[#E4E4E7] shadow-sm">
        <CardContent className="p-0">
          {loading ? <p className="p-4 text-sm text-[#52525B]">Loading...</p> : filteredInvoices.length === 0 ? (
            <p className="p-8 text-center text-sm text-[#52525B]">No invoices found</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[#E4E4E7]">
                  <TableHead className="w-8 px-2">
                    {canDelete && (() => {
                      const eligible = filteredInvoices.filter(canDeleteRow);
                      const allTicked = eligible.length > 0 && eligible.every(i => bulkSelected.has(i.id));
                      return (
                        <input
                          type="checkbox"
                          checked={allTicked}
                          disabled={eligible.length === 0}
                          onChange={e => {
                            if (e.target.checked) {
                              setBulkSelected(new Set(eligible.map(i => i.id)));
                            } else {
                              setBulkSelected(new Set());
                            }
                          }}
                          className="h-3.5 w-3.5 rounded border-[#3B82F6] accent-[#3B82F6] cursor-pointer disabled:cursor-not-allowed"
                          title={eligible.length === 0 ? 'No invoices eligible for bulk delete' : (allTicked ? 'Clear selection' : 'Select all (deletable only)')}
                          data-testid="bulk-select-all-invoices"
                        />
                      );
                    })()}
                  </TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold whitespace-nowrap">Date</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Invoice</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Customer</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Services</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Svc Charge</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Govt Fee</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Total</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Due</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Payment</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Work Status</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Remarks</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Assigned To</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">By</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-center w-12">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  // ---------------------------------------------------------
                  // Grouping logic for the history view.
                  //   - Today's invoices: always rendered fully.
                  //   - Same-month, older days: collapsible day groups.
                  //   - Older months: collapsible month → day → invoice tree.
                  // The +/− chevron in the leftmost cell toggles each group.
                  // ---------------------------------------------------------
                  const today = new Date();
                  const todayKey = today.toISOString().split('T')[0];
                  const currentMonthKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;

                  // Bucketise — preserve incoming sort order (newest first).
                  const todayInvs = [];
                  const olderDaysCurrentMonth = new Map(); // dayKey -> [inv]
                  const olderMonths = new Map();           // monthKey -> Map(dayKey -> [inv])

                  const invSeq = (n) => parseInt((String(n || '').match(/(\d+)\s*$/) || [])[1] || '0', 10);
                  const byNumber = [...filteredInvoices].sort((a, b) =>
                    (invSeq(b.invoice_number) - invSeq(a.invoice_number)) || (b.created_at || '').localeCompare(a.created_at || ''));
                  for (const inv of byNumber) {
                    const d = new Date(inv.created_at);
                    const dayKey = d.toISOString().split('T')[0];
                    const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                    if (dayKey === todayKey) {
                      todayInvs.push(inv);
                    } else if (monthKey === currentMonthKey) {
                      if (!olderDaysCurrentMonth.has(dayKey)) olderDaysCurrentMonth.set(dayKey, []);
                      olderDaysCurrentMonth.get(dayKey).push(inv);
                    } else {
                      if (!olderMonths.has(monthKey)) olderMonths.set(monthKey, new Map());
                      const dayMap = olderMonths.get(monthKey);
                      if (!dayMap.has(dayKey)) dayMap.set(dayKey, []);
                      dayMap.get(dayKey).push(inv);
                    }
                  }

                  // Number of leading columns to span (matches the header):
                  // Select + Date + Invoice + Customer + Services + Svc + Govt
                  // + Total + Due + Payment + Work + Remarks + Mode + By
                  // + Actions = 15
                  const COLS = 15;
                  const fmtMonth = (key) => {
                    const [y, m] = key.split('-');
                    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
                  };
                  const fmtDay = (key) => new Date(key).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });

                  const renderInvoiceRow = (inv) => {
                    const svcCharge = inv.items?.reduce((s, i) => s + i.service_charge * i.quantity, 0) || 0;
                    const govtFee = inv.items?.reduce((s, i) => s + i.govt_fee * i.quantity, 0) || 0;
                    const deletable = canDeleteRow(inv);
                    return (
                      <TableRow key={inv.id} className={`border-[#E4E4E7] ${bulkSelected.has(inv.id) ? 'bg-[#EFF6FF]' : ''}`}>
                        <TableCell className="w-8 px-2">
                          {canDelete && (
                            <input
                              type="checkbox"
                              checked={bulkSelected.has(inv.id)}
                              disabled={!deletable}
                              onChange={e => {
                                setBulkSelected(prev => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(inv.id); else next.delete(inv.id);
                                  return next;
                                });
                              }}
                              className="h-3.5 w-3.5 rounded border-[#3B82F6] accent-[#3B82F6] cursor-pointer disabled:cursor-not-allowed disabled:opacity-30"
                              title={deletable ? 'Select for bulk delete' : 'Invoice has payments — cannot bulk delete'}
                              data-testid={`bulk-select-invoice-${inv.invoice_number}`}
                            />
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-[#52525B] whitespace-nowrap">{new Date(inv.created_at).toLocaleDateString('en-IN')}</TableCell>
                        <TableCell className="text-xs font-mono text-[#3B82F6]">
                          {inv.invoice_number}
                          {inv.reference_number && (
                            <div className="text-[10px] text-[#10B981]" title={`Converted from token ${inv.reference_number}`}>
                              ref: {inv.reference_number}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          {inv.customer_id ? (
                            <button
                              type="button"
                              onClick={() => setLedgerCustomer({ id: inv.customer_id, name: inv.customer_name })}
                              className="text-[#0A0A0A] hover:text-[#3B82F6] hover:underline cursor-pointer text-left"
                              title={`Open ledger for ${inv.customer_name}`}
                              data-testid={`invoice-customer-link-${inv.id}`}
                            >
                              {inv.customer_name}
                            </button>
                          ) : inv.customer_name}
                          {/* Applicant chip — shown only when the applicant
                              differs from the billed customer (family member
                              / delegate / govt scheme applicant). Trimmed +
                              case-insensitive compare so trailing spaces or
                              casing tweaks don't falsely trigger it. */}
                          {(() => {
                            const appl = (inv.applicant_name || '').trim();
                            const cust = (inv.customer_name || '').trim();
                            if (!appl || appl.toLowerCase() === cust.toLowerCase()) return null;
                            return (
                              <div
                                className="text-[10px] text-[#7C3AED] truncate mt-0.5"
                                title={`Applicant: ${appl}`}
                                data-testid={`invoice-applicant-${inv.id}`}
                              >
                                ({appl})
                              </div>
                            );
                          })()}
                          {/* Phone underneath the name — click-to-call on
                              mobile, kept dim so it doesn't compete with
                              the primary customer name link above. The
                              WhatsApp icon right next to it opens wa.me
                              with a pre-typed bill-aware message. */}
                          {phoneByCustomer[inv.customer_id] && (() => {
                            const raw = String(phoneByCustomer[inv.customer_id]).replace(/\D/g, '');
                            // 10-digit numbers default to +91 (India). Longer
                            // numbers are kept verbatim so non-IN customers
                            // also work via Settings → country-coded phones.
                            const waPhone = raw.length === 10 ? `91${raw}` : raw;
                            const due = Number(inv.balance_due || 0);
                            const center = companyName || 'our centre';
                            // Resolve admin-defined template; fall back to a
                            // bill-aware default that uses the same {if_due}
                            // / {if_paid} block syntax so the conditional
                            // behaviour is identical regardless of override.
                            const defaultMsg = (
                              `Hello {customer_name},\n\n` +
                              `{if_due}This is a friendly reminder that bill {invoice_number} of ₹{total} from {center} has an outstanding balance of ₹{due}. Kindly arrange the payment at your earliest.{/if_due}` +
                              `{if_paid}Thank you for your business with {center}. Your bill {invoice_number} of ₹{total} is fully settled.{/if_paid}`
                            );
                            let tpl = (waTemplate || defaultMsg);
                            // Conditional block expansion. Blocks keep their
                            // inner content only when the condition holds;
                            // otherwise the entire block (including tags) is
                            // stripped. Run BEFORE token substitution so a
                            // user-supplied {customer_name} value that
                            // happens to contain "{if_due}" can never break
                            // the parser.
                            const expandBlock = (src, tag, keep) => {
                              const open = `{${tag}}`;
                              const close = `{/${tag}}`;
                              const re = new RegExp(open.replace(/[{}]/g, '\\$&') + '([\\s\\S]*?)' + close.replace(/[{}]/g, '\\$&'), 'g');
                              return src.replace(re, (_, inner) => keep ? inner : '');
                            };
                            tpl = expandBlock(tpl, 'if_due', due > 0);
                            tpl = expandBlock(tpl, 'if_paid', due <= 0.005);
                            const services = (inv.items || []).map(i => i.service_name).filter(Boolean).join(', ');
                            const filled = tpl
                              .replaceAll('{customer_name}', inv.customer_name || '')
                              .replaceAll('{invoice_number}', inv.invoice_number || '')
                              .replaceAll('{total}', String(inv.total ?? ''))
                              .replaceAll('{due}', String(due))
                              .replaceAll('{paid}', String(inv.amount_paid ?? 0))
                              .replaceAll('{date}', new Date(inv.created_at).toLocaleDateString('en-IN'))
                              .replaceAll('{center}', center)
                              .replaceAll('{services}', services);
                            const waText = encodeURIComponent(filled);
                            return (
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <a
                                  href={`tel:${raw}`}
                                  className="text-[10px] text-[#52525B] hover:text-[#3B82F6] font-mono"
                                  onClick={(e) => e.stopPropagation()}
                                  data-testid={`invoice-customer-phone-${inv.id}`}
                                >
                                  {phoneByCustomer[inv.customer_id]}
                                </a>
                                <a
                                  href={`https://wa.me/${waPhone}?text=${waText}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center text-[#16A34A] hover:text-[#15803D]"
                                  title={`Send a WhatsApp message about ${inv.invoice_number}`}
                                  onClick={(e) => e.stopPropagation()}
                                  data-testid={`invoice-customer-whatsapp-${inv.id}`}
                                >
                                  <WhatsappLogo size={13} weight="fill" />
                                </a>
                                {due > 0 && razorpayReady && (
                                  <button
                                    type="button"
                                    className="inline-flex items-center text-[#4F46E5] hover:text-[#4338CA]"
                                    title="Send online payment link (Razorpay) on WhatsApp"
                                    onClick={(e) => { e.stopPropagation(); sharePayLink(inv, waPhone); }}
                                    data-testid={`invoice-pay-link-${inv.id}`}
                                  >
                                    <CreditCard size={13} weight="fill" />
                                  </button>
                                )}
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="text-xs text-[#52525B] max-w-[200px]">
                          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                            {(inv.items || []).map((it, i) => {
                              const link = serviceLinkById[it.service_id];
                              return (
                                <span key={i} className="inline-flex items-center gap-1">
                                  {link ? (
                                    <a href={link} target="_blank" rel="noopener noreferrer"
                                      title={`Open ${it.service_name} portal`}
                                      data-testid={`inv-link1-${inv.id}-${i}`}
                                      className="inline-flex items-center justify-center w-5 h-5 rounded bg-[#E0F2FE] text-[#0369A1] hover:bg-[#BAE6FD] shrink-0">
                                      <LinkSimple size={11} weight="bold" />
                                    </a>
                                  ) : null}
                                  <span className="truncate">{it.service_name}{i < (inv.items.length - 1) ? ',' : ''}</span>
                                </span>
                              );
                            })}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-right font-mono">{fmt(svcCharge)}</TableCell>
                        <TableCell className="text-xs text-right font-mono">{fmt(govtFee)}</TableCell>
                        <TableCell className="text-xs text-right font-mono font-semibold">{fmt(inv.total)}</TableCell>
                        <TableCell className="text-xs text-right font-mono">
                          {inv.balance_due > 0 ? (
                            <span className="text-[#EA580C] font-semibold">{fmt(inv.balance_due)}</span>
                          ) : inv.balance_due < 0 ? (
                            <span className="text-[#8B5CF6] font-semibold" title="Overpayment">+{fmt(Math.abs(inv.balance_due))}</span>
                          ) : (
                            <span className="text-[#10B981]">Nil</span>
                          )}
                        </TableCell>
                        <TableCell><Badge className={`rounded-md text-[10px] capitalize ${statusColors[inv.status]}`} variant="outline">{statusLabel[inv.status] || inv.status}</Badge></TableCell>
                        <TableCell>
                          <button onClick={() => toggleWork(inv)} className="cursor-pointer" data-testid={`toggle-work-${inv.id}`}>
                            <Badge className={`rounded-md text-[10px] capitalize cursor-pointer ${workColors[inv.work_status || 'pending']}`} variant="outline">{workLabel[inv.work_status || 'pending'] || inv.work_status}</Badge>
                          </button>
                        </TableCell>
                        <TableCell className="text-xs text-[#52525B] max-w-[120px] truncate" title={inv.notes || inv.items?.map(i => i.remark).filter(Boolean).join(', ') || ''}>{inv.notes || inv.items?.map(i => i.remark).filter(Boolean).join(', ') || '-'}</TableCell>
                        <TableCell className="text-xs text-[#52525B]">
                          {(() => {
                            // Show unique assignee names across all items on the invoice.
                            const names = [...new Set((inv.items || []).map(i => staffById[i.assigned_to] || '').filter(Boolean))];
                            return names.length ? names.join(', ') : <span className="text-[#A1A1AA]">—</span>;
                          })()}
                        </TableCell>
                        <TableCell className="text-xs text-[#52525B]">{inv.created_by_name || '-'}</TableCell>
                        <TableCell className="text-center">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 rounded-full hover:bg-[#F4F4F5] data-[state=open]:bg-[#F4F4F5]" data-testid={`actions-menu-${inv.id}`} aria-label="Actions">
                                <DotsThreeVertical size={16} weight="bold" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-44 rounded-lg">
                              <DropdownMenuItem onClick={() => handleMakeDraft(inv)} data-testid={`make-draft-${inv.id}`} className="cursor-pointer text-xs">
                                <Eye size={14} className="mr-2" /> Make Draft
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setLedgerInvoice(inv)} data-testid={`ledger-invoice-${inv.id}`} className="cursor-pointer text-xs">
                                <BookOpen size={14} className="mr-2" /> Ledger
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => printInvoice(inv)} data-testid={`print-invoice-${inv.id}`} className="cursor-pointer text-xs">
                                <Printer size={14} className="mr-2" /> Print
                              </DropdownMenuItem>
                              {canEdit && (
                                <DropdownMenuItem onClick={() => navigate(`/invoices/edit/${inv.id}`)} data-testid={`edit-invoice-${inv.id}`} className="cursor-pointer text-xs">
                                  <PencilSimple size={14} className="mr-2" /> Edit
                                </DropdownMenuItem>
                              )}
                              {canEdit && (
                                // Duplicate — opens a fresh Create Invoice
                                // pre-filled with this bill's customer +
                                // items. Invoice number, payments and work
                                // status are intentionally NOT carried over.
                                <DropdownMenuItem
                                  onClick={() => navigate(`/invoices/create?duplicate_from=invoice:${inv.id}`)}
                                  data-testid={`duplicate-invoice-${inv.id}`}
                                  className="cursor-pointer text-xs text-[#7C3AED] focus:text-[#7C3AED]"
                                >
                                  <Copy size={14} className="mr-2" /> Duplicate
                                </DropdownMenuItem>
                              )}
                              {inv.balance_due > 0 && canEdit && (
                                <DropdownMenuItem onClick={() => setPayDialog(inv)} data-testid={`pay-invoice-${inv.id}`} className="cursor-pointer text-xs text-[#10B981] focus:text-[#10B981]">
                                  <CurrencyInr size={14} className="mr-2" /> Record Payment
                                </DropdownMenuItem>
                              )}
                              {canDelete && !((inv.amount_paid || 0) > 0 || (inv.payment_history && inv.payment_history.length > 0)) && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => handleDelete(inv)} data-testid={`delete-invoice-${inv.id}`} className="cursor-pointer text-xs text-[#EF4444] focus:text-[#EF4444]">
                                    <Trash size={14} className="mr-2" /> Delete
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  };

                  const sumTotals = (invs) => invs.reduce((acc, inv) => {
                    acc.total += inv.total || 0;
                    acc.due += Math.max(0, inv.balance_due || 0);
                    // Track service charge + govt fee separately so the day /
                    // month header rows can break the billed amount down into
                    // its two components — surfaced as `(Svc ₹X · Govt ₹Y)`
                    // after the Due figure.
                    const svc = (inv.items || []).reduce((s, i) => s + (i.service_charge || 0) * (i.quantity || 0), 0);
                    const gov = (inv.items || []).reduce((s, i) => s + (i.govt_fee || 0) * (i.quantity || 0), 0);
                    acc.svc += svc;
                    acc.gov += gov;
                    return acc;
                  }, { total: 0, due: 0, svc: 0, gov: 0 });

                  const dayHeaderRow = (dayKey, invs, indent = 0) => {
                    const open = expandedDays.has(dayKey);
                    const sums = sumTotals(invs);
                    return (
                      <TableRow key={`day-${dayKey}`} className="bg-[#F9FAFB] hover:bg-[#F4F4F5] border-[#E4E4E7] cursor-pointer" onClick={() => toggleDay(dayKey)} data-testid={`day-header-${dayKey}`}>
                        <TableCell colSpan={COLS} className={`text-xs py-2 ${indent ? 'pl-10' : 'pl-4'}`}>
                          <div className="flex items-center gap-2">
                            {open ? <CaretDown size={14} weight="bold" className="text-[#3B82F6]" /> : <CaretRight size={14} weight="bold" className="text-[#52525B]" />}
                            <span className="font-semibold text-[#0A0A0A]">{fmtDay(dayKey)}</span>
                            <span className="text-[#A1A1AA] text-[11px]">·</span>
                            <span className="text-[#52525B]">{invs.length} invoice{invs.length === 1 ? '' : 's'}</span>
                            {summaryVisible && (<>
                            <span className="text-[#A1A1AA] text-[11px]">·</span>
                            <span className="font-mono text-[#52525B]" data-testid={`day-billed-${dayKey}`}>Billed {fmt(sums.total)}</span>
                            {sums.due > 0 ? (
                              <span className="font-mono text-[#EA580C]">
                                Due {fmt(sums.due)}
                                <span className="text-[10px] text-[#52525B] font-normal ml-1">(Svc {fmt(sums.svc)} · Govt {fmt(sums.gov)})</span>
                              </span>
                            ) : (
                              <span className="text-[10px] text-[#52525B] font-normal">(Svc {fmt(sums.svc)} · Govt {fmt(sums.gov)})</span>
                            )}
                            </>)}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  };

                  const monthHeaderRow = (monthKey, dayMap) => {
                    const open = expandedMonths.has(monthKey);
                    const allInvs = Array.from(dayMap.values()).flat();
                    const sums = sumTotals(allInvs);
                    return (
                      <TableRow key={`month-${monthKey}`} className="bg-[#EFF6FF] hover:bg-[#DBEAFE] border-[#E4E4E7] cursor-pointer" onClick={() => toggleMonth(monthKey)} data-testid={`month-header-${monthKey}`}>
                        <TableCell colSpan={COLS} className="text-xs py-2 pl-4">
                          <div className="flex items-center gap-2">
                            {open ? <CaretDown size={14} weight="bold" className="text-[#2563EB]" /> : <CaretRight size={14} weight="bold" className="text-[#2563EB]" />}
                            <span className="font-bold uppercase tracking-[0.12em] text-[10px] text-[#1D4ED8]">{fmtMonth(monthKey)}</span>
                            <span className="text-[#A1A1AA] text-[11px]">·</span>
                            <span className="text-[#1D4ED8] text-xs">{dayMap.size} day{dayMap.size === 1 ? '' : 's'}, {allInvs.length} invoice{allInvs.length === 1 ? '' : 's'}</span>
                            {summaryVisible && (<>
                            <span className="text-[#A1A1AA] text-[11px]">·</span>
                            <span className="font-mono text-[#1D4ED8]" data-testid={`month-billed-${monthKey}`}>Billed {fmt(sums.total)}</span>
                            {sums.due > 0 ? (
                              <span className="font-mono text-[#EA580C]">
                                Due {fmt(sums.due)}
                                <span className="text-[10px] text-[#1D4ED8]/80 font-normal ml-1">(Svc {fmt(sums.svc)} · Govt {fmt(sums.gov)})</span>
                              </span>
                            ) : (
                              <span className="text-[10px] text-[#1D4ED8]/80 font-normal">(Svc {fmt(sums.svc)} · Govt {fmt(sums.gov)})</span>
                            )}
                            </>)}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  };

                  const rows = [];
                  // Flat table when:
                  //   • a payment-status filter is active (Paid/Partial/Credit/Overpaid)
                  //   • the user has typed anything into the search box
                  // In both cases the user wants every match in one glance
                  // without expanding day/month headers. Press Escape to
                  // clear the search and fall back to the grouped view.
                  if (filter !== 'all' || search.trim().length > 0) {
                    filteredInvoices.forEach(inv => rows.push(renderInvoiceRow(inv)));
                    return rows;
                  }
                  // 1) Today's invoices — fully expanded.
                  todayInvs.forEach(inv => rows.push(renderInvoiceRow(inv)));
                  // 2) Older days within current month.
                  Array.from(olderDaysCurrentMonth.keys()).sort().reverse().forEach(dayKey => {
                    rows.push(dayHeaderRow(dayKey, olderDaysCurrentMonth.get(dayKey)));
                    if (expandedDays.has(dayKey)) {
                      olderDaysCurrentMonth.get(dayKey).forEach(inv => rows.push(renderInvoiceRow(inv)));
                    }
                  });
                  // 3) Older months (newest month first).
                  Array.from(olderMonths.keys()).sort().reverse().forEach(monthKey => {
                    rows.push(monthHeaderRow(monthKey, olderMonths.get(monthKey)));
                    if (expandedMonths.has(monthKey)) {
                      Array.from(olderMonths.get(monthKey).keys()).sort().reverse().forEach(dayKey => {
                        rows.push(dayHeaderRow(dayKey, olderMonths.get(monthKey).get(dayKey), 1));
                        if (expandedDays.has(dayKey)) {
                          olderMonths.get(monthKey).get(dayKey).forEach(inv => rows.push(renderInvoiceRow(inv)));
                        }
                      });
                    }
                  });
                  return rows;
                })()}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Payment — uses the shared PaymentReceiptDialog so the experience
          matches the Pay button on Edit Invoice (full-page popup with
          customer Apply-Credit shortcut). */}
      <PaymentReceiptDialog
        open={!!payDialog}
        onOpenChange={(v) => { if (!v) setPayDialog(null); }}
        invoice={payDialog}
        receipt={null}
        accounts={finAccounts}
        onSaved={() => { setPayDialog(null); fetchInvoices(); }}
      />

      {/* Detail Dialog */}
      <Dialog open={!!detailDialog} onOpenChange={() => setDetailDialog(null)}>
        <DialogContent className="rounded-xl border-[#E4E4E7] max-w-lg">
          <DialogHeader><DialogTitle>{detailDialog?.invoice_number}</DialogTitle><DialogDescription>Invoice for {detailDialog?.customer_name}</DialogDescription></DialogHeader>
          {detailDialog && (
            <div className="space-y-3 text-sm">
              <Table>
                <TableHeader><TableRow><TableHead>Service</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Charge</TableHead><TableHead className="text-right">Govt Fee</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
                <TableBody>{detailDialog.items.map((it, i) => (<TableRow key={i}><TableCell>{it.service_name}</TableCell><TableCell className="text-right">{it.quantity}</TableCell><TableCell className="text-right font-mono">{fmt(it.service_charge)}</TableCell><TableCell className="text-right font-mono">{fmt(it.govt_fee)}</TableCell><TableCell className="text-right font-mono font-semibold">{fmt(it.total)}</TableCell></TableRow>))}</TableBody>
              </Table>
              <div className="flex justify-between pt-2 border-t border-[#E4E4E7]">
                <span className="font-semibold">Total: {fmt(detailDialog.total)}</span>
                <span>Paid: <span className="text-[#10B981] font-semibold">{fmt(detailDialog.amount_paid)}</span></span>
                <span>Due: <span className="text-[#EF4444] font-semibold">{fmt(detailDialog.balance_due)}</span></span>
              </div>
              <Button onClick={() => printInvoice(detailDialog)} variant="outline" className="w-full rounded-xl" data-testid="print-detail-btn"><Printer size={14} className="mr-2" /> Print Invoice</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}>
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Delete</AlertDialogTitle>
            <AlertDialogDescription>{confirmDelete?.label}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg" data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteAction} className="bg-[#EF4444] text-white hover:bg-[#DC2626] rounded-lg" data-testid="confirm-delete-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <InvoiceLedgerDialog
        invoice={ledgerInvoice}
        onOpenChange={(o) => { if (!o) setLedgerInvoice(null); }}
        accounts={finAccounts}
        canEdit={canEdit}
        onChanged={() => { fetchInvoices(); fetchDrafts(); fetchTokens(); }}
      />

      {/* Customer ledger popup — opened by clicking a customer name in the
          Tokens / Drafts / Invoices tables. Closing it leaves the user on
          /invoices (no navigation away). "Collect Due" intentionally
          hidden here — the Invoice Mgmt screen is a reference view; due
          collection should flow through the Receipts page or the ledger
          surfaced from inside Edit Invoice. */}
      <CustomerLedgerDialog
        open={!!ledgerCustomer}
        customer={ledgerCustomer}
        onOpenChange={(o) => { if (!o) setLedgerCustomer(null); }}
        variant="full"
        showCollectDue={false}
        useFullReceipt={true}
        onEditInvoice={(invoiceId) => navigate(`/invoices/edit/${invoiceId}`)}
        onChanged={() => { fetchInvoices(); fetchDrafts(); }}
      />
    </div>
  );
}
